import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { TrendingDown, DollarSign, Calendar, CheckCircle, Wallet } from 'lucide-react';

const Results = ({ results, debts, mortgage, consolidationRate }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatMonths = (months) => {
    const years = Math.floor(months / 12);
    const remainingMonths = Math.round(months % 12);
    if (years === 0) return `${remainingMonths} months`;
    if (remainingMonths === 0) return `${years} years`;
    return `${years} years ${remainingMonths} months`;
  };

  // Minimum repayment over a default 30-year term (industry-standard
  // presentation — the term itself is not displayed to the client).
  const principal = results.consolidated.newMortgageAmount || 0;
  const annualRate = (Number(consolidationRate) || 0) / 100;
  const monthlyRate = annualRate / 12;
  const months = 30 * 12;
  const monthlyMin = monthlyRate > 0
    ? (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1)
    : principal / months;
  const fortnightlyMin = monthlyMin * 12 / 26;
  const weeklyMin = monthlyMin * 12 / 52;

  return (
    <div className="mt-12 space-y-8">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Your Reduction Breakdown</h2>
        <p className="text-gray-600">Here's how debt consolidation can benefit you</p>
      </div>

      {/* Debt Consolidation Reduction Section */}
      <Card className="border-2 border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50">
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-blue-500 text-white rounded-full p-2">
              <TrendingDown className="h-6 w-6" />
            </div>
            <CardTitle className="text-2xl">Debt Consolidation Reduction</CardTitle>
          </div>
          <CardDescription className="text-base">Immediate reduction from consolidating your debts</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-sm text-gray-600 mb-1">Total Debt Amount</div>
              <div className="text-2xl font-bold text-gray-900">
                {formatCurrency(results.current.totalDebtAmount)}
              </div>
              <div className="text-xs text-gray-500 mt-1">Being consolidated</div>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-sm text-gray-600 mb-1">Average Interest Rate</div>
              <div className="text-2xl font-bold text-red-600">
                {results.current.averageRate.toFixed(2)}%
              </div>
              <div className="text-xs text-gray-500 mt-1">Current weighted average</div>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-sm text-gray-600 mb-1">New Consolidation Rate</div>
              <div className="text-2xl font-bold text-green-600">
                {Number(consolidationRate || 0).toFixed(2)}%
              </div>
              <div className="text-xs text-gray-500 mt-1">Single mortgage rate</div>
            </div>
          </div>

          <div className="mt-6 grid md:grid-cols-2 gap-4">
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-700">Monthly Payment Reduction</span>
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div className="text-3xl font-bold text-green-600 mb-1">
                {formatCurrency(results.consolidated.monthlySavings)}
              </div>
              <div className="text-sm text-gray-600">
                From {formatCurrency(results.current.totalMonthlyPayment)} to {formatCurrency(results.consolidated.monthlyPayment)}
              </div>
              <div className="text-xs text-green-600 font-medium mt-2">
                Reduce {((results.consolidated.monthlySavings / results.current.totalMonthlyPayment) * 100).toFixed(1)}% monthly
              </div>
            </div>

            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-700">Total Interest Reduction</span>
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div className="text-3xl font-bold text-green-600 mb-1">
                {formatCurrency(results.consolidated.totalInterestSavings)}
              </div>
              <div className="text-sm text-gray-600">
                Just from consolidation alone
              </div>
              <div className="text-xs text-green-600 font-medium mt-2">
                Before even using redraw strategy
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Minimum Repayment options on the new consolidated loan */}
      <Card className="border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-teal-50" data-testid="min-repayments-card">
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-emerald-600 text-white rounded-full p-2">
              <Wallet className="h-6 w-6" />
            </div>
            <CardTitle className="text-2xl">Minimum Total Repayments</CardTitle>
          </div>
          <CardDescription className="text-base">
            Minimum amount required on the new consolidated loan of {formatCurrency(principal)} at {Number(consolidationRate || 0).toFixed(2)}%.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4 shadow-sm border border-emerald-200">
              <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Weekly</div>
              <div className="text-2xl font-bold text-emerald-700 mt-1" data-testid="min-weekly">
                {formatCurrency(weeklyMin)}
              </div>
              <div className="text-xs text-gray-500 mt-1">per week</div>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm border border-emerald-200">
              <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Fortnightly</div>
              <div className="text-2xl font-bold text-emerald-700 mt-1" data-testid="min-fortnightly">
                {formatCurrency(fortnightlyMin)}
              </div>
              <div className="text-xs text-gray-500 mt-1">every 2 weeks</div>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm border border-emerald-200">
              <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Monthly</div>
              <div className="text-2xl font-bold text-emerald-700 mt-1" data-testid="min-monthly">
                {formatCurrency(monthlyMin)}
              </div>
              <div className="text-xs text-gray-500 mt-1">per month</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Highlights */}
      <div className="grid md:grid-cols-3 gap-6">
        <Card className="border-2 border-green-200 bg-green-50">
          <CardHeader>
            <DollarSign className="h-8 w-8 text-green-600 mb-2" />
            <CardTitle className="text-2xl">
              {formatCurrency(results.consolidated.monthlySavings)}
            </CardTitle>
            <CardDescription>Monthly Payment Reduction</CardDescription>
          </CardHeader>
        </Card>

        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardHeader>
            <TrendingDown className="h-8 w-8 text-blue-600 mb-2" />
            <CardTitle className="text-2xl">
              {formatCurrency(results.redrawStrategy.totalSavings)}
            </CardTitle>
            <CardDescription>Total Interest Reduction</CardDescription>
          </CardHeader>
        </Card>

        <Card className="border-2 border-purple-200 bg-purple-50">
          <CardHeader>
            <Calendar className="h-8 w-8 text-purple-600 mb-2" />
            <CardTitle className="text-2xl">
              {results.redrawStrategy.yearsSaved} years
            </CardTitle>
            <CardDescription>Time Saved with Redraw</CardDescription>
          </CardHeader>
        </Card>
      </div>

      {/* Detailed Comparison */}
      <Card>
        <CardHeader>
          <CardTitle>Detailed Comparison</CardTitle>
          <CardDescription>Side-by-side breakdown of your options</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Scenario</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Monthly Payment</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Total Interest</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Payoff Time</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <div className="font-medium">Current Situation</div>
                    <div className="text-sm text-gray-500">Separate debts + mortgage</div>
                  </td>
                  <td className="text-right py-3 px-4 font-medium">
                    {formatCurrency(results.current.totalMonthlyPayment)}
                  </td>
                  <td className="text-right py-3 px-4">
                    {formatCurrency(results.current.totalInterest)}
                  </td>
                  <td className="text-right py-3 px-4">Various</td>
                </tr>
                <tr className="border-b hover:bg-gray-50 bg-blue-50">
                  <td className="py-3 px-4">
                    <div className="font-medium">Consolidated</div>
                    <div className="text-sm text-gray-500">All debts in mortgage</div>
                  </td>
                  <td className="text-right py-3 px-4 font-medium">
                    {formatCurrency(results.consolidated.monthlyPayment)}
                    <div className="text-sm text-green-600">
                      Reduce {formatCurrency(results.consolidated.monthlySavings)}/mo
                    </div>
                  </td>
                  <td className="text-right py-3 px-4">
                    {formatCurrency(results.consolidated.totalInterest)}
                  </td>
                  <td className="text-right py-3 px-4">Standard term</td>
                </tr>
                <tr className="hover:bg-gray-50 bg-green-50">
                  <td className="py-3 px-4">
                    <div className="font-medium flex items-center gap-2">
                      With Redraw Strategy
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    </div>
                    <div className="text-sm text-gray-500">Using monthly reduction for extra payments</div>
                  </td>
                  <td className="text-right py-3 px-4 font-medium">
                    {formatCurrency(results.redrawStrategy.monthlyPayment)}
                    <div className="text-sm text-blue-600">Same total spend</div>
                  </td>
                  <td className="text-right py-3 px-4">
                    {formatCurrency(results.redrawStrategy.totalInterest)}
                    <div className="text-sm text-green-600">
                      Reduce {formatCurrency(results.redrawStrategy.additionalSavings)}
                    </div>
                  </td>
                  <td className="text-right py-3 px-4">
                    <div className="font-medium text-green-600">
                      {formatMonths(results.redrawStrategy.monthsSaved)} earlier
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Explanation Card */}
      <Card className="bg-gradient-to-br from-slate-50 to-slate-100">
        <CardHeader>
          <CardTitle>How the Redraw Strategy Works</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-700">
            By consolidating your high-interest debts into your mortgage, you immediately reduce your
            monthly payment by <strong>{formatCurrency(results.consolidated.monthlySavings)}</strong>.
          </p>
          <p className="text-gray-700">
            Instead of spending this reduction, deposit it into a redraw account linked to your mortgage.
            This effectively maintains your current payment level of{' '}
            <strong>{formatCurrency(results.current.totalMonthlyPayment)}</strong>, but now it's all
            going towards your mortgage at a lower interest rate.
          </p>
          <p className="text-gray-700">
            The result: You'll pay off your mortgage{' '}
            <strong className="text-green-600">{results.redrawStrategy.yearsSaved} years earlier</strong>{' '}
            and reduce your interest by <strong className="text-green-600">{formatCurrency(results.redrawStrategy.totalSavings)}</strong>{' '}
            in total.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Results;
